import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsIsoDateTime,
  parseAsJson,
  parseAsString,
  ParserBuilder,
} from 'nuqs'
import { ZodType } from 'zod'

import { StorableTypesMap, StorableType, BaseValue } from '../validation'
import { CreateParserOptions, createParserWithValidation } from './createUrlParser'

export const urlParsersMap: {
  [TType in StorableType]: ParserBuilder<StorableTypesMap[TType]>
} = {
  number: parseAsFloat,
  string: parseAsString,
  date: parseAsIsoDateTime,
  boolean: parseAsBoolean,
  list_string: parseAsArrayOf(parseAsString),
  list_number: parseAsArrayOf(parseAsFloat),
  list_object: parseAsArrayOf(parseAsJson((value: unknown) => value as object)),
  object: parseAsJson((value: unknown) => value as object),
}

function getNuqsParserByType<TType extends StorableType, TValue extends BaseValue<TType>>(
  type: TType,
) {
  return urlParsersMap[type] as ParserBuilder<TValue>
}

export function getParserWithValidation<
  TType extends StorableType,
  TValue extends BaseValue<TType>,
>(
  options: CreateParserOptions & {
    type: TType
    scheme: ZodType<TValue> | null
    defaultValue: TValue | null
    clearOnDefault: boolean | undefined
  },
) {
  const { onValidationError, scheme, type, defaultValue, clearOnDefault } = options

  const defaultNuqsParser = getNuqsParserByType<TType, TValue>(type)

  const parserWithValidation = createParserWithValidation<TValue>(defaultNuqsParser, scheme, {
    onValidationError,
  })

  if (!defaultValue) {
    return parserWithValidation
  }

  return parserWithValidation.withDefault(defaultValue).withOptions({ clearOnDefault })
}
